import { useState } from 'react'
import { UserRoles } from '@truepill/tpos-types'
import AuthLimited from 'components/AuthLimited'
import CloseButton from 'components/CloseButton'
import PrescriberSearchOptions from 'components/PrescriberSearch'
import { useFormData } from 'hooks/useFormData'
import styled from 'styled-components'
import type { Prescriber, Prescription } from 'types'
import { checkIfProvided } from 'utils'

interface SupervisingPrescriberCardProps {
  viewMode: 'display' | 'edit'
}

const SupervisingPrescriberCard = ({ viewMode }: SupervisingPrescriberCardProps): JSX.Element => {
  const {
    state: { formData },
    actions: { updateFormData },
  } = useFormData()

  const [showSearch, setShowSearch] = useState(false)

  const prescriptionFormData = formData.prescription as Prescription
  const supervisingPrescriber = prescriptionFormData.supervisingPrescriber

  const hasSupervisingPrescriber = !!supervisingPrescriber?._id

  const supervisingPrescriberName = supervisingPrescriber
    ? checkIfProvided(
        `${supervisingPrescriber?.firstName} ${supervisingPrescriber?.lastName}${
          supervisingPrescriber?.suffix ? `, ${supervisingPrescriber?.suffix}` : ''
        }`,
      )
    : 'None provided'

  const handleSupervisingPrescriberSelect = (selectProps: {
    prescriberId: Prescriber['_id']
    prescriberNpi: Prescriber['npi']
    prescriber: Prescriber
  }) => {
    updateFormData({
      prescription: {
        supervisingPrescriber: { $set: selectProps.prescriber },
        supervisingPrescriberId: { $set: selectProps.prescriberId },
      },
    })
    setShowSearch(false)
  }

  if (viewMode === 'display') {
    return <DisplayContainer>{supervisingPrescriberName}</DisplayContainer>
  }

  if (hasSupervisingPrescriber) {
    // Show existing supervising prescriber - It can't be edited nor removed
    return (
      <EditContainer>
        <CardContainer>
          <CardHeader>{supervisingPrescriberName}</CardHeader>
          <CardDetail>
            <strong>NPI:</strong> {supervisingPrescriber?.npi || 'N/A'}
          </CardDetail>
        </CardContainer>
      </EditContainer>
    )
  } else {
    // No supervising prescriber - allow adding for specified roles
    return (
      <AuthLimited
        roles={[
          UserRoles.Admin,
          UserRoles.Technician,
          UserRoles.Pharmacist,
          UserRoles.LeadPharmacist,
          UserRoles.CustomerSupport,
          UserRoles.LeadCustomerSupport,
        ]}
        fallback={<DisplayContainer>None provided</DisplayContainer>}
      >
        {showSearch ? (
          <SearchContainer>
            <PrescriberSearchOptions isModal={false} onSelect={handleSupervisingPrescriberSelect} />
            <CancelButton onClick={() => setShowSearch(false)}>Cancel</CancelButton>
          </SearchContainer>
        ) : (
          <AddButton onClick={() => setShowSearch(true)}>+ Add Supervising Prescriber</AddButton>
        )}
      </AuthLimited>
    )
  }
}

// Styled Components
const DisplayContainer = styled.div`
  color: inherit;
  font-size: inherit;
`

const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CardContainer = styled.div`
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  position: relative;
  border: 1px solid #ddd;
`

const CardHeader = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
`

const CardDetail = styled.div`
  font-size: 0.9em;
  color: #666;
`

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AddButton = styled.button`
  background: none;
  border: 1px dashed #007bff;
  color: #007bff;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #f8f9fa;
  }
`

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  align-self: flex-start;

  &:hover {
    background: #5a6268;
  }
`

export default SupervisingPrescriberCard
